import type { ServicePageData } from "$lib/types/service-detail";

const clippingPathMedia = {
  heroBicycle: {
    src: "/images/clipping-path/clipping-path-hero-precision-bicycle-vector-path.webp",
    alt: "Bicycle outlined with precise Photoshop pen-tool path points",
    width: 1122,
    height: 1402,
  },
  heroApparel: {
    src: "/images/clipping-path/clipping-path-hero-apparel-tshirt-pen-path.webp",
    alt: "Brown T-shirt outlined with precise Photoshop pen-tool path points",
    width: 1122,
    height: 1402,
  },
  heroFootwear: {
    src: "/images/clipping-path/clipping-path-hero-footwear-sneaker-pen-path.webp",
    alt: "Pair of dark sneakers outlined with detailed pen-tool path points",
    width: 1122,
    height: 1402,
  },
  introPathExamples: {
    src: "/images/clipping-path/clipping-path-intro-multi-product-pen-path-examples.webp",
    alt: "Pen-tool path examples around handbags and footwear, including close-ups of leather and stitching",
    width: 1122,
    height: 1402,
  },
  comparisonOriginal: {
    src: "/images/clipping-path/clipping-path-comparison-industrial-server-rack-before.webp",
    alt: "Raw industrial machinery server rack photographed in a workshop before clipping path",
    width: 1500,
    height: 2000,
  },
  comparisonClipped: {
    src: "/images/clipping-path/clipping-path-comparison-industrial-server-rack-after.webp",
    alt: "Finished industrial machinery server rack cutout with complex vector path isolating all pipes, frames, and components",
    width: 1500,
    height: 2000,
  },
  showcaseWatch: {
    src: "/images/clipping-path/clipping-path-showcase-luxury-watch-compound-path.webp",
    alt: "Luxury watch with separate precision clipping paths around its case, dial, and details",
    width: 1122,
    height: 1402,
  },
  showcaseFashion: {
    src: "/images/clipping-path/clipping-path-showcase-fashion-model-multi-channel-path.webp",
    alt: "Fashion model and handbag outlined with separate clipping paths for garment details",
    width: 1122,
    height: 1402,
  },
  foodFlatlayBefore: {
    src: "/images/clipping-path/product-food-cereal-granola-muesli-flatlay-berries-before.webp",
    alt: "Granola cereal, fresh berries, and breakfast ingredients flatlay before clipping path",
    width: 1445,
    height: 1806,
  },
  foodFlatlayAfter: {
    src: "/images/clipping-path/product-food-cereal-granola-muesli-flatlay-berries-after.webp",
    alt: "Granola cereal, fresh berries, and breakfast ingredients cleanly isolated with precision hand-drawn clipping paths",
    width: 1445,
    height: 1806,
  },
} as const;

export const clippingPathPage: ServicePageData = {
  slug: "clipping-path",
  seo: {
    title:
      "Clipping Path Services | Studio Click House",
    description:
      "Hand-drawn Photoshop clipping paths with compound paths, color separation, and pre-press-ready PSD or TIFF files for catalogs and agencies.",
  },
  hero: {
    title: "Clipping",
    titleAccent: "Path.",
    theme: "light",
    aspectRatio: "4/5",
    description:
      "Hand-drawn vector paths created in Adobe Photoshop at high magnification, supplied in layered PSD or pre-press TIFF files for print and digital use.",
    media: clippingPathMedia.heroBicycle,
    supportingMedia: [
      clippingPathMedia.heroApparel,
      clippingPathMedia.heroFootwear,
    ],
  },
  intro: {
    heading: "What is hand-crafted clipping path?",
    paragraphs: [
      "A clipping path is a closed vector path created with Photoshop's Pen Tool to isolate a 2D image from its background. Our retouchers plot smooth anchor points along natural object boundaries at high magnification for clean print and digital output.",
    ],
    stages: [
      {
        label: "Sub-Pixel Bezier Curves",
        description:
          "Anchor points are plotted along natural inner object edges to avoid background color spill or harsh cutout borders.",
        media: clippingPathMedia.introPathExamples,
      },
      {
        label: "Multi-Path Layer Isolation",
        description:
          "We create independent named sub-paths for buttons, collars, soles, and dials for selective color shifting and retouching.",
      },
      {
        label: "Pre-Press & CMYK Preparation",
        description:
          "Paths are saved as active clipping paths with designated flatness settings ready for InDesign, Illustrator, and web pipelines.",
      },
    ],
  },
  beforeAfter: {
    heading: "Clipping path before and after",
    description:
      "Compare the original machinery photo with the finished clipping path, keeping the frame, pipes, and components cleanly defined.",
    bullets: [
      "Trace smooth paths around complex contours",
      "Keep narrow gaps and inner edges clearly defined",
      "Prepare clean silhouettes for production use",
    ],
    beforeSrc: clippingPathMedia.comparisonOriginal.src,
    beforeAlt: clippingPathMedia.comparisonOriginal.alt,
    afterSrc: clippingPathMedia.comparisonClipped.src,
    afterAlt: clippingPathMedia.comparisonClipped.alt,
    beforeLabel: "Before",
    afterLabel: "After",
    width: clippingPathMedia.comparisonClipped.width,
    height: clippingPathMedia.comparisonClipped.height,
    layout: "cards",
    textPosition: "right",
  },
  showcase: {
    heading: "Multi-Path Clipping for Complex Products",
    theme: "light",
    description:
      "From detailed watch components to layered fashion and accessories, each element gets its own precise path for flexible editing and clean production output.",
    bullets: [
      "Separate product components into editable paths",
      "Preserve fine edges around hardware, clothing, and accessories",
      "Deliver organized paths for retouching and production",
    ],
    beforeAfter: {
      before: {
        ...clippingPathMedia.showcaseWatch,
        label: "Multi-path",
      },
      after: {
        ...clippingPathMedia.showcaseFashion,
        label: "Multi-path",
      },
    },
  },
  additionalBeforeAfter: [
    {
      sectionId: "clipping-path-complex-food-comparison",
      heading: "Complex food & flatlay clipping paths",
      description:
        "Isolate intricate multi-item food compositions—from scattered berries and delicate flakes to packaging and utensils—with sub-pixel hand-drawn vector paths.",
      bullets: [
        "Isolate individual ingredients and loose items cleanly",
        "Maintain natural organic contours and seed details",
        "Deliver ready-to-composite cutouts for packaging & advertising",
      ],
      beforeSrc: clippingPathMedia.foodFlatlayBefore.src,
      beforeAlt: clippingPathMedia.foodFlatlayBefore.alt,
      afterSrc: clippingPathMedia.foodFlatlayAfter.src,
      afterAlt: clippingPathMedia.foodFlatlayAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      width: clippingPathMedia.foodFlatlayAfter.width,
      height: clippingPathMedia.foodFlatlayAfter.height,
      layout: "cards",
      textPosition: "right",
    },
  ],
  productTypes: [
    {
      id: "upholstered-chair",
      code: "01",
      title: "Upholstered Furniture",
      subtitle: "Trace curved chair silhouettes and detailed upholstery edges.",
      src: "/images/clipping-path/clipping-path-product-upholstered-accent-chair.webp",
      alt: "Upholstered accent chair outlined with a precision clipping path",
    },
    {
      id: "diamond-ring",
      code: "02",
      title: "Fine Jewelry",
      subtitle: "Define gemstone settings and inner openings with compound paths.",
      src: "/images/clipping-path/clipping-path-product-diamond-solitaire-ring.webp",
      alt: "Diamond ring with separate outer and inner clipping paths",
    },
    {
      id: "baseball-cap",
      code: "03",
      title: "Caps & Headwear",
      subtitle: "Follow the crown and curved brim through detailed contours.",
      src: "/images/clipping-path/clipping-path-product-teal-baseball-cap.webp",
      alt: "Teal baseball cap outlined with a precision clipping path",
    },
    {
      id: "model-straw-hat",
      code: "04",
      title: "On-Model Fashion",
      subtitle: "Isolate the model, hair, and hat with a clean connected silhouette.",
      src: "/images/clipping-path/clipping-path-product-model-straw-hat.webp",
      alt: "Fashion model wearing a straw hat outlined with a clipping path",
    },
    {
      id: "designer-sunglasses",
      code: "05",
      title: "Eyewear",
      subtitle: "Separate frame contours, lenses, and open spaces accurately.",
      src: "/images/clipping-path/clipping-path-product-designer-sunglasses.webp",
      alt: "Designer sunglasses with frame and lens contours traced for clipping",
    },
    {
      id: "zebra-print-pump",
      code: "06",
      title: "Patterned Footwear",
      subtitle: "Keep pointed toes, heels, and cut-ins crisp around bold patterns.",
      src: "/images/clipping-path/clipping-path-product-zebra-print-high-heel.webp",
      alt: "Zebra-print pointed pump outlined with a precision clipping path",
    },
  ],
  features: {
    heading: "Why top production studios choose our paths",
    items: [
      {
        icon: "PenTool",
        title: "Hand-Drawn Vector Precision",
        description:
          "Every path is hand-plotted with smooth bezier handles placed exactly where curves transition naturally.",
      },
      {
        icon: "FolderTree",
        title: "Named Multi-Path Layers",
        description:
          "We organize complex products into named sub-paths (e.g. 'Body', 'Sole', 'Laces', 'Logo') for quick color grading.",
      },
      {
        icon: "Sliders",
        title: "Inward 0.5px Edge Offsets",
        description:
          "Paths are drawn 0.5px inside object boundaries to completely eliminate background color bleed on dark backdrops.",
      },
      {
        icon: "Clock",
        title: "Overnight Production SLAs",
        description:
          "Our 24/7 studio guarantees turnaround within 12 to 24 hours even for batches exceeding 1,000 complex files.",
      },
      {
        icon: "FileCheck",
        title: "InDesign & Pre-Press Ready",
        description:
          "Embedded clipping paths with flat curve tolerances ready for immediate desktop publishing and CMYK separation.",
      },
      {
        icon: "ShieldAlert",
        title: "Zero Automation Artifacts",
        description:
          "We reject automated AI magic-wand selection tools to ensure zero fuzzy pixels or chopped edges.",
      },
    ],
  },
  faqs: [
    {
      question: "What is the difference between single path and multi-path?",
      answer:
        "A single clipping path isolates the outer boundary of an object from its background. A multi-path contains multiple independent vector paths drawn around different components (such as laces, sole, logo, or collar), allowing art directors to independently color grade or manipulate each part.",
    },
    {
      question: "Will the vector path be embedded into the file?",
      answer:
        "Yes. We deliver layered PSD or TIFF files with active, named Photoshop Paths saved in the Paths panel, compatible with Adobe Illustrator, InDesign, and Photoshop.",
    },
    {
      question: "How do you avoid background color fringing?",
      answer:
        "Our retouchers zoom in to 300% and manually place bezier points approximately 0.5 to 1 pixel inside the subject boundary, ensuring no background halos remain when the product is placed onto a new background.",
    },
    {
      question: "Can you handle complex items like jewelry and bicycles?",
      answer:
        "Yes, our artists specialize in super-complex pathing involving dozens of internal hole cutouts, spokes, mesh fabrics, and intricate gemstone prongs.",
    },
    {
      question: "What is the turnaround time for a batch of 500 images?",
      answer:
        "Batches of up to 500 images are typically completed within 24 hours. Express 12-hour turnaround is also available upon request.",
    },
  ],
  cta: {
    heading: "Need clean vector clipping paths?",
    description:
      "Send us 3 sample images and experience our hand-crafted pen tool precision firsthand with a free trial edit.",
    steps: [
      {
        title: "Submit sample files",
        description:
          "Share up to 3 test files with your path naming or knockout instructions.",
      },
      {
        title: "Evaluate path accuracy",
        description:
          "Inspect our anchor point placement and curve smoothness at 300% zoom.",
      },
      {
        title: "Scale your workflow",
        description:
          "Submit entire seasonal production runs with 24/7 delivery assurance.",
      },
    ],
  },
};
